import { useEffect, useState } from 'react';
import { AppTable, AppCheckbox, AppButton, AppPagination } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type { ReturnRezeptRow } from '@/services/main/returnRezept/types';
import type { ColumnsType } from 'antd/es/table';
import styles from './ReturnRezeptTable.module.css';

/**
 * BD 8.3 返戻・問合せレセプト登録 — 状態制御.
 * status = 0 (未印刷) → thao tác được. status ≠ 0 (1 = 印刷済み) → khoá.
 * Cùng một điều kiện chi phối CẢ checkbox LẪN button 「再提出年月登録」 của dòng đó,
 * nên để chung một chỗ thay vì lặp lại phép so sánh (HIT-11330).
 */
export const isRowActionable = (row: ReturnRezeptRow): boolean => row.status === 0;
/** 返戻一覧の1ページあたり表示件数（HIT-11331 で 5 から変更）。 */
const PAGE_SIZE = 30;

interface ReturnRezeptTableProps {
  list: ReturnRezeptRow[];
  isLoading: boolean;
  selectedRowKeys: number[];
  onToggleRow: (id: number, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onOpenResubmissionModal: (rezeptId: number) => void;
}

export default function ReturnRezeptTable({
  list,
  isLoading,
  selectedRowKeys,
  onToggleRow,
  onSelectAll,
  onOpenResubmissionModal,
}: ReturnRezeptTableProps) {
  const navigate = useNavigate();
  const { serviceType } = useTheme();
  const isA2 = serviceType === SERVICE_TYPE_A2;
  const massagerLabel = isA2 ? 'はり・きゅう師' : 'マッサージ師';

  const [currentPage, setCurrentPage] = useState(1);
  // HIT-11331: 1ページの表示件数を 5 → 30 に変更（顧客要望）。
  // BD 8.3 は「common の総件数・ページネーションを使用」としか書いておらず件数を定めていない。
  // 共通定数 DEFAULT_PER_PAGE は 20 で、顧客指定の 30 とは異なるため、この画面固有の値として持つ。
  const pageSize = PAGE_SIZE;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(list.length / pageSize));
    setCurrentPage(page => Math.min(page, maxPage));
  }, [list.length]);

  const selectableRows = list.filter(isRowActionable);
  const [headerOverride, setHeaderOverride] = useState(false);
  const allSelectableChecked =
    selectableRows.length > 0
      ? selectableRows.every(r => selectedRowKeys.includes(r.id))
      : headerOverride;

  const columns: ColumnsType<ReturnRezeptRow> = [
    {
      title: (
        <AppCheckbox
          checked={allSelectableChecked}
          onChange={e => {
            setHeaderOverride(e.target.checked);
            onSelectAll(e.target.checked);
          }}
          disabled={list.length === 0}
        />
      ),
      dataIndex: 'id',
      key: 'selection',
      width: 56,
      align: 'center',
      render: (_: unknown, record: ReturnRezeptRow) => (
        // The row itself toggles on click (HIT-14703); keep the checkbox's own click from
        // reaching the row, otherwise one click would toggle twice.
        <span onClick={e => e.stopPropagation()}>
          <AppCheckbox
            checked={selectedRowKeys.includes(record.id)}
            disabled={!isRowActionable(record)}
            onChange={e => {
              e.stopPropagation();
              onToggleRow(record.id, e.target.checked);
            }}
          />
        </span>
      ),
    },
    {
      title: '受診者番号',
      dataIndex: 'serial_number',
      render: (_: unknown, record: ReturnRezeptRow) => (
        <PatientEditLink
          patientId={record.patient_id}
          value={record.serial_number}
          stopPropagation
        />
      ),
    },
    {
      title: '受診者名',
      dataIndex: 'patient_name',
    },
    {
      title: '保険者番号',
      dataIndex: 'insurer_number',
    },
    {
      title: '施術年月',
      dataIndex: 'treatement_yearmonth_jp',
    },
    {
      // HIT-11329: BD 8.3 返戻・問合せレセプト登録 項目12 — 施術年月 と 本・関 の間。
      // 「再提出年月一括登録」で登録した月をここで確認する列。未登録なら空欄
      // (ReturnRezeptResource が rezept_yearmonth = null のとき '' を返す)。
      title: '再提出年月',
      dataIndex: 'rezept_yearmonth_jp',
    },
    {
      title: '本・関',
      dataIndex: 'rezept_kbn',
      render: (kbn: number) => (kbn === 2 ? '関' : '本'),
    },
    {
      title: '同意書',
      dataIndex: 'consent_serial_number',
    },
    {
      title: '保険証・訪問変更',
      dataIndex: 'is_locked',
      render: (locked: number) => (locked === 0 ? '可' : '不可'),
    },
    {
      title: massagerLabel,
      dataIndex: 'massager_name',
    },
    {
      title: '操作',
      key: 'action',
      align: 'center',
      width: 220,
      render: (_: unknown, record: ReturnRezeptRow) => {
        // HIT-3536: 施術入力 → /visit_clinic/edit/{patient_id}/{wareki_yearmonth}
        // 訪問入力 → /visits/schedule/{massager_id}/{first_visit_date} (week of first visit).
        // Fallback to /visits/schedule if data missing so the button still navigates.
        const visitClinicPath = `../visit_clinic/edit/${record.patient_id}/${encodeURIComponent(record.treatement_yearmonth_jp)}`;
        const visitSchedulePath =
          record.massager_id && record.first_visit_date
            ? `../visits/schedule/${record.massager_id}/${record.first_visit_date}`
            : `../visits/schedule`;
        return (
          <div className="mx-auto grid w-full max-w-[188px] grid-cols-2 gap-1">
            <AppButton
              type="primary"
              size="small"
              className="col-span-2 w-full"
              // HIT-11330: BD「状態制御」— checkbox 無効時（status = 1 / 印刷済み）は
              // 「再提出年月登録」ボタンも無効化する。dòng đã in rồi thì không được
              // đăng ký lại 再提出年月 qua nút của dòng (luồng 一括登録 vốn đã lọc status = 0).
              disabled={!isRowActionable(record)}
              onClick={e => {
                e.stopPropagation();
                onOpenResubmissionModal(record.id);
              }}
            >
              再提出年月登録
            </AppButton>
            <AppButton
              size="small"
              className="w-full"
              onClick={e => {
                e.stopPropagation();
                navigate(visitClinicPath);
              }}
            >
              施術入力
            </AppButton>
            <AppButton
              size="small"
              className="w-full"
              onClick={e => {
                e.stopPropagation();
                navigate(visitSchedulePath);
              }}
            >
              訪問入力
            </AppButton>
          </div>
        );
      },
    },
  ];

  // HIT-14703 (UAT No179) — BD 全選択 / 各行checkbox の行背景色:
  //   checkbox無効 (status = 1 / 印刷済み) → グレー
  //   checkbox有効 (status = 0 / 未印刷) → Check時 skyblue / Uncheck時 白
  const rowClassNameFor = (record: ReturnRezeptRow): string => {
    if (!isRowActionable(record)) return styles.rowDisabled;
    return selectedRowKeys.includes(record.id) ? styles.rowChecked : styles.rowUnchecked;
  };

  const totalCount = list.length;
  const totalCountLabel = `該当件数: ${totalCount} 件`;

  return (
    <>
      <div className="mb-2 text-sm text-gray-700">{totalCountLabel}</div>
      <div className={styles.returnTable}>
        <AppTable<ReturnRezeptRow>
          columns={columns}
          dataSource={list.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
          rowKey="id"
          loading={isLoading}
          bordered
          pagination={false}
          rowClassName={rowClassNameFor}
          onRow={record => ({
            // BD 各行checkbox: 行内の任意位置クリックでもcheckboxをtoggle可能とする (有効時のみ).
            onClick: () => {
              if (!isRowActionable(record)) return;
              onToggleRow(record.id, !selectedRowKeys.includes(record.id));
            },
            style: { cursor: isRowActionable(record) ? 'pointer' : 'default' },
          })}
        />
      </div>
      <AppPagination
        className="mt-2"
        current={currentPage}
        total={list.length}
        pageSize={pageSize}
        onChange={setCurrentPage}
      />
    </>
  );
}
